import { SearchUserHit } from 'lib-shared-types-api';
import { useMemo } from 'react';

import { useAppSelector } from 'utils/redux/useAppSelector';

const selectPresenceOverlay = (state: {
  presenceOverlay: { members: Record<string, Partial<SearchUserHit>> };
}) => state.presenceOverlay.members;

/**
 * Merges presence overlay data with search results.
 *
 * This hook reads from the Redux presenceOverlay slice and merges any overlay
 * updates with the provided members array. This allows real-time presence
 * updates to be displayed without mutating RTK Query cache.
 *
 * @example
 *   const { members } = searchApi.useReadAdvancedSearchResultsQuery(...);
 *   const membersWithPresence = useMembersWithPresence(members);
 *   // membersWithPresence now has real-time is_online, is_working, etc.
 *
 * @param members - Array of SearchUserHit from RTK Query (can be readonly)
 * @returns Array of SearchUserHit with presence overlay data merged in
 */
export const useMembersWithPresence = <T extends SearchUserHit>(
  members: readonly T[],
): T[] => {
  const presenceOverlay = useAppSelector(selectPresenceOverlay);

  return useMemo(() => {
    return members.map((member) => {
      const overlay = presenceOverlay[member.id];
      if (!overlay) return member;
      return { ...member, ...overlay };
    });
  }, [members, presenceOverlay]);
};
